import React, { useState, useEffect, useRef } from "react";
import WeatherEmailSettingWrapper from "./WeatherEmailSetting.style";
import IntlMessages from "@iso/components/utility/intlMessages";
import {
  Form,
  Input,
  Button,
  Row,
  Col,
  Select,
  Checkbox,
  Divider,
  Switch,
  TimePicker,
  Alert,
  Typography,
  Spin,
  Modal,
  message
} from "antd";
import { MailOutlined, TeamOutlined, ArrowLeftOutlined, SearchOutlined } from "@ant-design/icons";
import { ReactComponent as PrecipTomorrowio } from "@iso/containers/Pages/WeatherMap/Controls/icons/precip-tomorrowio.svg";
import { ReactComponent as TemperatureTomorrowio } from "@iso/containers/Pages/WeatherMap/Controls/icons/temperature-tomorrowio.svg";
import { ReactComponent as FeelsLikeTomorrowio } from "@iso/containers/Pages/WeatherMap/Controls/icons/feels-like-tomorrowio.svg";
import { ReactComponent as HumidityTomorrowio } from "@iso/containers/Pages/WeatherMap/Controls/icons/humidity-tomorrowio.svg";
import { ReactComponent as WindSpeedTomorrowio } from "@iso/containers/Pages/WeatherMap/Controls/icons/wind-speed-tomorrowio.svg";
import { ReactComponent as WindGustTomorrowio } from "@iso/containers/Pages/WeatherMap/Controls/icons/wind-gust-tomorrowio.svg";
import { ReactComponent as WindDirectionTomorrowio } from "@iso/containers/Pages/WeatherMap/Controls/icons/wind-direction-tomorrowio.svg";
import { ReactComponent as PressureTomorrowio } from "@iso/containers/Pages/WeatherMap/Controls/icons/pressure-tomorrowio.svg";
import { ReactComponent as RainAccTomorrowio } from "@iso/containers/Pages/WeatherMap/Controls/icons/rain-acc-tomorrowio.svg";
import { ReactComponent as VisibilityTomorrowio } from "@iso/containers/Pages/WeatherMap/Controls/icons/visibility-tomorrowio.svg";
import { ReactComponent as CloudCoverTomorrowio } from "@iso/containers/Pages/WeatherMap/Controls/icons/cloud-cover-tomorrowio.svg";
import { ReactComponent as CloudBaseTomorrowio } from "@iso/containers/Pages/WeatherMap/Controls/icons/cloud-base-tomorrowio.svg";
import { ReactComponent as CloudCeilingTomorrowio } from "@iso/containers/Pages/WeatherMap/Controls/icons/cloud-ceiling-tomorrowio.svg";
import { weatherCategory } from "@iso/constants/weatherCategory";
import _ from "lodash";
import {weatherEmailSettingService} from '../../.././services/weatheremailsetting.service'
import { useSelector } from 'react-redux';

const { Option } = Select;
const { Text } = Typography;

const MONTH_OPTIONS = [
  { value: 1, label: "January" },
  { value: 2, label: "February" },
  { value: 3, label: "March" },
  { value: 4, label: "April" },
  { value: 5, label: "May" },
  { value: 6, label: "June" },
  { value: 7, label: "July" },
  { value: 8, label: "August" },
  { value: 9, label: "September" },
  { value: 10, label: "October" },
  { value: 11, label: "November" },
  { value: 12, label: "December" },
];

const INITIAL_DATA = {
  is_send_daily: false,
  is_send_weekly: false,
  weather_types: [],
  months: MONTH_OPTIONS.map((item) => item.value),
}

const WeatherEmailSetting = ({isShowEmailSetting, setIsShowEmailSetting}) => {
  const activePropertyId = useSelector((state) => state.property.activePropertyId);
  const activePermission = useSelector((state) => state.permission.activePermission);
  const [form] = Form.useForm();
  const [fields, setFields] = useState([]);
  const [loading, setLoading] = useState(false);
  const [weatherEmailSetting, setWeatherEmailSetting] = useState(INITIAL_DATA);

  // Recipients state
  const [propertyUsers, setPropertyUsers] = useState([]);
  const [selectedRecipients, setSelectedRecipients] = useState([]);
  const [recipientsLoading, setRecipientsLoading] = useState(false);

  // Screen switching state
  const [currentScreen, setCurrentScreen] = useState('settings');
  const [recipientSearch, setRecipientSearch] = useState('');

  // Saved state refs for unsaved-changes detection
  const savedSettingRef = useRef(INITIAL_DATA);
  const savedRecipientsRef = useRef([]);

  const isOwnerOrAdmin = activePermission === 'owner' || activePermission === 'admin';

  const [weatherTypes, setWeatherTypes] = useState({
    precipitationIntensity: {
      key: "precipitationIntensity",
      label: "Precip. Intensity",
      unit: "mm/hr",
      icon: PrecipTomorrowio,
      category: weatherCategory.rain.value,
    },
    temperature: {
      key: "temperature",
      label: "Temperature",
      unit: "°C",
      icon: TemperatureTomorrowio,
      category: weatherCategory.temp.value,
    },
    temperatureApparent: {
      key: "temperatureApparent",
      label: "Feels Like",
      unit: "°C",
      icon: FeelsLikeTomorrowio,
      category: weatherCategory.temp.value,
    },
    humidity: {
      key: "humidity",
      label: "Humidity",
      unit: "%",
      icon: HumidityTomorrowio,
      category: weatherCategory.temp.value,
    },
    windSpeed: {
      key: "windSpeed",
      label: "Wind Speed",
      unit: "km/h",
      icon: WindSpeedTomorrowio,
      category: weatherCategory.wind.value,
    },
    windGust: {
      key: "windGust",
      label: "Wind Gust",
      unit: "km/h",
      icon: WindGustTomorrowio,
      category: weatherCategory.wind.value,
    },
    windDirection: {
      key: "windDirection",
      label: "Wind Direction",
      unit: "",
      icon: WindDirectionTomorrowio,
      category: weatherCategory.wind.value,
    },
    pressureSeaLevel: {
      key: "pressureSeaLevel",
      label: "Pressure",
      value: "",
      unit: "",
      icon: PressureTomorrowio,
      category: weatherCategory.rain.value,
    },
    rainAccumulation: {
      key: "rainAccumulation",
      label: "Rain Acc",
      unit: "mm",
      icon: RainAccTomorrowio,
      category: weatherCategory.rain.value,
    },
    visibility: {
      key: "visibility",
      label: "Visibility",
      unit: "km",
      icon: VisibilityTomorrowio,
      category: weatherCategory.rain.value,
    },
    cloudCover: {
      key: "cloudCover",
      label: "Cloud Cover",
      unit: "%",
      icon: CloudCoverTomorrowio,
      category: weatherCategory.cloud.value,
    },
    cloudBase: {
      key: "cloudBase",
      label: "Cloud Base",
      unit: "ft",
      icon: CloudBaseTomorrowio,
      category: weatherCategory.cloud.value,
    },
    cloudCeiling: {
      key: "cloudCeiling",
      label: "Cloud Ceiling",
      unit: "ft",
      icon: CloudCeilingTomorrowio,
      category: weatherCategory.cloud.value,
    },
  });

  const handleChangeWeatherType = (weatherType, isCheck) => {
    const newWeatherEmailSetting = { ...weatherEmailSetting };
    if (isCheck) {
      newWeatherEmailSetting.weather_types = [...newWeatherEmailSetting.weather_types, weatherType];
    } else {
      newWeatherEmailSetting.weather_types = newWeatherEmailSetting.weather_types.filter(
        (item) => item !== weatherType
      );
    }
    setWeatherEmailSetting(newWeatherEmailSetting);
  };

  const renderWeatherTypeCheckboxes = (category) => {
    const weatherTypeCheckboxs = [];
    for (const [key, value] of Object.entries(weatherTypes)) {
      if (value.category !== category) continue;
      weatherTypeCheckboxs.push(
        <div>
          <Checkbox
            key={key}
            checked={weatherEmailSetting.weather_types.includes(value.key)}
            onChange={(e) =>
              handleChangeWeatherType(value.key, e.target.checked)
            }
          >
            <div className="group-icon">
              <value.icon width={24} height={24} />
              <p>{value.label}</p>
            </div>
          </Checkbox>
        </div>
      );
    }
    return weatherTypeCheckboxs;
  };

  const handleChangeMonths = (months) => {
    const newWeatherEmailSetting = { ...weatherEmailSetting };
    newWeatherEmailSetting.months = months;
    setWeatherEmailSetting(newWeatherEmailSetting);
  };

  const onChangeSwitch = (checked, type) => {
    const newWeatherEmailSetting = { ...weatherEmailSetting };
    newWeatherEmailSetting[type] = checked;
    setWeatherEmailSetting(newWeatherEmailSetting);
  };

  const handleSubmit = () => {
    if(!activePropertyId) return message.error("Please select property")
    setLoading(true);
    const { weather_email_recipients, ...settingData } = weatherEmailSetting;
    const data = {
      ...settingData,
      property_id: activePropertyId,
      months: JSON.stringify(weatherEmailSetting.months),
      weather_types: JSON.stringify(weatherEmailSetting.weather_types),
      weather_email_recipients: JSON.stringify(selectedRecipients),
    }
    weatherEmailSettingService.create(data).then((res) => {
      if (res.code === "0000") {
        message.success("Saved !");
        savedSettingRef.current = _.cloneDeep(weatherEmailSetting);
        savedRecipientsRef.current = [...selectedRecipients];
      } else {
        message.error(res.error);
      }
      setLoading(false);
    }).catch((err) => {
      console.log('err', err)
      setLoading(false);
    });
  }

  const getWeatherEmailSetting = () => {
    if(!activePropertyId) return
    setLoading(true);

    weatherEmailSettingService.getByPropertyId(activePropertyId).then((res) => {
      if (res.code === "0000") {
        const data = res.data ? res.data : INITIAL_DATA;
        setWeatherEmailSetting(data);
        savedSettingRef.current = _.cloneDeep(data);
      } else {
        message.error(res.error);
      }
      setLoading(false);
    }).catch((err) => {
      console.log('err', err)
      setLoading(false);
    });
  }

  const loadRecipientsData = async () => {
    if (!activePropertyId) return;
    setRecipientsLoading(true);
    try {
      const [usersRes, recipientsRes] = await Promise.all([
        weatherEmailSettingService.getPropertyUsers(activePropertyId),
        weatherEmailSettingService.getRecipients(activePropertyId),
      ]);

      if (usersRes.code === '0000' && usersRes.users) {
        setPropertyUsers(usersRes.users);
      }

      if (recipientsRes.code === '0000' && recipientsRes.recipients) {
        setSelectedRecipients(recipientsRes.recipients);
        savedRecipientsRef.current = [...recipientsRes.recipients];
      } else {
        setSelectedRecipients([]);
        savedRecipientsRef.current = [];
      }
    } catch (err) {
      console.error('Error loading recipients data:', err);
    } finally {
      setRecipientsLoading(false);
    }
  };

  const hasUnsavedChanges = () => {
    const saved = savedSettingRef.current;
    const current = weatherEmailSetting;
    if (saved.is_send_daily !== current.is_send_daily) return true;
    if (saved.is_send_weekly !== current.is_send_weekly) return true;
    if (!_.isEqual(saved.weather_types, current.weather_types)) return true;
    if (!_.isEqual(saved.months, current.months)) return true;
    if (!_.isEqual(savedRecipientsRef.current, selectedRecipients)) return true;
    return false;
  };

  const handleClose = () => {
    if (hasUnsavedChanges()) {
      Modal.confirm({
        title: 'Unsaved Changes',
        content: 'You have unsaved changes. Are you sure you want to close without saving?',
        okText: 'Discard',
        okButtonProps: { style: { backgroundColor: '#0B2A1B', borderColor: '#0B2A1B' } },
        cancelText: 'Go Back',
        onOk: () => {
          setWeatherEmailSetting(savedSettingRef.current);
          setSelectedRecipients(savedRecipientsRef.current);
          setCurrentScreen('settings');
          setRecipientSearch('');
          setIsShowEmailSetting(false);
        },
      });
    } else {
      setCurrentScreen('settings');
      setRecipientSearch('');
      setIsShowEmailSetting(false);
    }
  };

  const getUserLabel = (user) => {
    if (user.name && user.email && user.name !== user.email) {
      return { name: user.name, email: user.email };
    }
    return { name: user.name || user.email || `User #${user.id}`, email: user.email || '' };
  };

  const filteredUsers = propertyUsers.filter((user) => {
    if (!recipientSearch) return true;
    const search = recipientSearch.toLowerCase();
    const label = getUserLabel(user);
    return label.name.toLowerCase().includes(search) || label.email.toLowerCase().includes(search);
  });

  const handleToggleRecipient = (userId) => {
    if (selectedRecipients.includes(userId)) {
      setSelectedRecipients(selectedRecipients.filter((id) => id !== userId));
    } else {
      setSelectedRecipients([...selectedRecipients, userId]);
    }
  };

  const handleSelectAll = () => {
    const filteredIds = filteredUsers.map((u) => u.id);
    const merged = [...new Set([...selectedRecipients, ...filteredIds])];
    setSelectedRecipients(merged);
  };

  const handleClearRecipients = () => {
    setSelectedRecipients([]);
  };

  useEffect(() => {
    getWeatherEmailSetting();
    loadRecipientsData();
  }, [activePropertyId])

  const settingsFooter = [
    <Button key="back" className="cancel-button" onClick={handleClose}>
      {<IntlMessages id="propertyPage.modal.cancel" />}
    </Button>,
    <Button
      key="submit"
      className="btn-success"
      type="primary"
      loading={loading}
      onClick={handleSubmit}
    >
      {<IntlMessages id="propertyPage.modal.save" />}
    </Button>,
  ];

  const recipientsFooter = [
    <Button
      key="back"
      icon={<ArrowLeftOutlined />}
      onClick={() => { setCurrentScreen('settings'); setRecipientSearch(''); }}
    >
      Back
    </Button>,
    <Button
      key="select-all"
      onClick={handleSelectAll}
      disabled={filteredUsers.length === 0}
    >
      Select All
    </Button>,
    <Button
      key="clear"
      onClick={handleClearRecipients}
      disabled={selectedRecipients.length === 0}
    >
      Clear
    </Button>,
  ];

  const renderSettingsScreen = () => (
    <>
      <Form
        form={form}
        name="addProperty"
        layout="horizontal"
        scrollToFirstError
        initialValues={{
          use_my_account: false,
        }}
        fields={fields}
      >
        <Form.Item
          label={<IntlMessages id="Send Email Daily" />}
          valuePropName="checked"
          tooltip={<IntlMessages id="weatherPage.weatherEmail.sendEmailDaily.tooltip" />}
        >
          <Switch onChange={ (checked) => onChangeSwitch(checked, "is_send_daily")} checked={weatherEmailSetting.is_send_daily ? true : false}/>
        </Form.Item>
        <Form.Item
          label={<IntlMessages id="Send Email Weekly" />}
          valuePropName="checked"
          tooltip={<IntlMessages id="weatherPage.weatherEmail.sendEmailWeekly.tooltip" />}
        >
          <Switch onChange={ (checked) => onChangeSwitch(checked, "is_send_weekly")} checked={weatherEmailSetting.is_send_weekly ? true : false}/>
        </Form.Item>
        <div className="weather-type">
          <p className="normal-label"><IntlMessages id="Select Weather Type:" /></p>
          <Row gutter={24}>
            {_.map(weatherCategory, (item, index) => {
              return (
                <Col className="here" span={12}>
                  <p>{item.label}</p>
                  {renderWeatherTypeCheckboxes(item.value)}
                </Col>
              );
            })}
          </Row>
        </div>
        <p className="normal-label">
          <IntlMessages id="Which months you'd like to receive email:" />
        </p>
        <Select
          id="month-select"
          mode="multiple"
          allowClear={true}
          placeholder="Please select month"
          value={weatherEmailSetting.months}
          options={MONTH_OPTIONS}
          onChange={handleChangeMonths}
        />
      </Form>

      {/* Email Recipients Section */}
      <Divider style={{ marginTop: 24 }}>
        <MailOutlined style={{ marginRight: 8 }} />
        Email Recipients
      </Divider>

      {!isOwnerOrAdmin && (
        <Alert
          message="Permission Required"
          description="Only property owners and admins can manage weather email recipients."
          type="warning"
          showIcon
          style={{ marginBottom: 16 }}
        />
      )}

      <p className="normal-label">
        Select which property users should receive weather forecast emails.
        If no recipients are selected, emails will be sent to all property users.
      </p>

      {recipientsLoading ? (
        <div style={{ textAlign: 'center', padding: '20px 0' }}>
          <Spin size="small" />
        </div>
      ) : (
        <>
          <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
            <Select
              mode="multiple"
              allowClear
              placeholder={isOwnerOrAdmin ? 'Select users to receive weather emails' : 'Requires owner or admin access'}
              value={selectedRecipients}
              onChange={(values) => setSelectedRecipients(values)}
              disabled={!isOwnerOrAdmin}
              style={{ flex: 1 }}
              optionFilterProp="children"
              filterOption={(input, option) =>
                option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
              }
            >
              {propertyUsers.map((user) => {
                const label = getUserLabel(user);
                return (
                  <Option key={user.id} value={user.id}>
                    {label.email && label.name !== label.email
                      ? `${label.name} (${label.email})`
                      : label.name}
                  </Option>
                );
              })}
            </Select>
            <Button
              icon={<TeamOutlined />}
              onClick={() => setCurrentScreen('recipients')}
              disabled={!isOwnerOrAdmin}
              title="Manage recipients"
            />
          </div>

          {selectedRecipients.length === 0 && isOwnerOrAdmin && (
            <Text type="secondary" style={{ display: 'block', marginBottom: 12, fontStyle: 'italic', fontSize: 13 }}>
              No recipients selected — emails will be sent to all property users.
            </Text>
          )}
        </>
      )}
    </>
  );

  const renderRecipientsScreen = () => (
    <div className="recipients-screen">
      <Input
        placeholder="Search users by name or email..."
        prefix={<SearchOutlined />}
        value={recipientSearch}
        onChange={(e) => setRecipientSearch(e.target.value)}
        allowClear
        style={{ marginBottom: 16 }}
      />

      <Text type="secondary" style={{ display: 'block', marginBottom: 12 }}>
        {selectedRecipients.length} of {propertyUsers.length} users selected
        {selectedRecipients.length === 0 && ' — emails will be sent to all property users'}
      </Text>

      <div className="recipients-list">
        {filteredUsers.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '20px 0', color: '#999' }}>
            No users found matching your search.
          </div>
        ) : (
          filteredUsers.map((user) => {
            const label = getUserLabel(user);
            const isSelected = selectedRecipients.includes(user.id);
            return (
              <div
                key={user.id}
                className={`recipient-item ${isSelected ? 'selected' : ''}`}
                onClick={() => handleToggleRecipient(user.id)}
              >
                <Checkbox checked={isSelected} />
                <div className="recipient-info">
                  <span className="recipient-name">{label.name}</span>
                  {label.email && label.name !== label.email && (
                    <span className="recipient-email">{label.email}</span>
                  )}
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );

  return (
    <WeatherEmailSettingWrapper
      forceRender={true}
      visible={isShowEmailSetting}
      title={currentScreen === 'settings'
        ? <IntlMessages id="weatherPage.weatherEmail.setting" />
        : <span><TeamOutlined style={{ marginRight: 8 }} />Manage Email Recipients</span>
      }
      maskClosable={true}
      onCancel={handleClose}
      footer={currentScreen === 'settings' ? settingsFooter : recipientsFooter}
    >
      {currentScreen === 'settings' ? renderSettingsScreen() : renderRecipientsScreen()}
    </WeatherEmailSettingWrapper>
  );
};

export default WeatherEmailSetting;
